import { useSelector } from 'react-redux';
import { ActionType, DataTestId, RouteName, TextKey } from '@mews-commander/enums';
import { lt } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import {
    Table,
    useTableState,
    IconName,
    Stack,
    TableHeader,
    TableProvider,
    ActionToolbarButton,
    Link,
} from '@optimus-web/core';
import { ModalWrapper, NewModalBodyWrapper } from '@mews-commander/ui';
import { ApplicationState, WithGuideLink, WithServiceId } from '@mews-commander/types';
import { PromotedServiceAssignment } from '@mews-commander/data-access';
import { Target } from '@mews/enums';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getServiceIndexUrl } from '@mews-commander/legacy-entities/src/selectors/services';
import { getLocalizedServiceName, getServiceEnterpriseId } from '@mews-commander/legacy-entities/src/ducks/services';
import { canCurrentProfileManageServiceConfigurations } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';

import { usePromotedServices } from '../queries';
import { useServicePromotionFormDialog } from '../hooks/useServicePromotionFormDialog';
import { useColumns } from './tableMapping';
import { ServicePromotionDialog } from './ServicePromotionDialog';

export const ServicePromotionList = ({ serviceId, guideLink }: WithServiceId & WithGuideLink) => {
    const [tableState, setTableState] = useTableState<PromotedServiceAssignment>({ pagination: { show: false } });
    const { data = [], isFetching } = usePromotedServices(serviceId);

    const enterpriseId = useSelector((state: ApplicationState) => getServiceEnterpriseId(state, { id: serviceId }));
    const enterpriseName = useSelector((state: ApplicationState) => getEnterpriseName(state, { enterpriseId }));
    const serviceName = useSelector((state: ApplicationState) => getLocalizedServiceName(state, { id: serviceId }));
    const canManageServiceConfiguration = useCommanderSelector<boolean>(canCurrentProfileManageServiceConfigurations);

    const { isFormOpen, openFormDialog, handleCloseDialog, openEditDialog, promotedService } =
        useServicePromotionFormDialog();

    const columns = useColumns(canManageServiceConfiguration, openEditDialog);

    const resolveRoute = useResolveRoute();
    const enterpriseUrl = resolveRoute(RouteName.EnterpriseDetail, { enterpriseId });
    const serviceDetailUrl = resolveRoute(RouteName.VisitDetail, { serviceId });
    const serviceIndexBreadcrumbUrl = useSelector((state: ApplicationState) =>
        getServiceIndexUrl(state, { enterpriseId, resolveRoute })
    );

    const breadcrumbs: Breadcrumbs = [
        {
            text: enterpriseName,
            url: enterpriseUrl,
        },
        {
            text: lt(TextKey.ServicePlural),
            url: serviceIndexBreadcrumbUrl,
        },
        {
            text: serviceName,
            url: serviceDetailUrl,
        },
        {
            text: lt(TextKey.PromotedServiceAssignmentPlural),
            active: true,
        },
    ];

    const actions: HeadingAction[] = [
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: lt(TextKey.Help),
            type: ActionType.Link,
        },
    ];

    return (
        <ModalWrapper>
            <ModalHeader title={lt(TextKey.BookableService)} actions={actions} breadcrumbs={breadcrumbs} />
            <NewModalBodyWrapper padded>
                <Stack spacing="400" vertical>
                    <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                        <Stack itemAlignment="center" distribution="space-between">
                            <TableHeader title={lt(TextKey.PromotedServiceAssignmentPlural)} />
                            {canManageServiceConfiguration ? (
                                <ActionToolbarButton
                                    icon={IconName.Add}
                                    onClick={openFormDialog}
                                    variant="tertiary"
                                    dataTestId={DataTestId.ServicePromotionsCreatePromotionButton}
                                >
                                    {lt(TextKey.PromotedServiceAssignmentAddButton)}
                                </ActionToolbarButton>
                            ) : null}
                        </Stack>
                        <Table
                            uniqueRowId="id"
                            data={data}
                            amountOfRows={data.length}
                            loading={isFetching}
                            emptyState={{
                                primaryText: lt(TextKey.PromotedServiceAssignmentEmptyStateTitle),
                                secondaryText: lt(TextKey.PromotedServiceAssignmentEmptyStateText),
                                action: (
                                    <Link target={Target.Blank} href={guideLink}>
                                        {lt(TextKey.PromotedServiceAssignmentEmptyStateLink)}
                                    </Link>
                                ),
                                maxWidth: '500px',
                            }}
                        />
                    </TableProvider>
                </Stack>
            </NewModalBodyWrapper>
            {canManageServiceConfiguration ? (
                <ServicePromotionDialog
                    key={promotedService?.id || 'create'}
                    active={isFormOpen}
                    onClose={handleCloseDialog}
                    enterpriseId={enterpriseId}
                    serviceId={serviceId}
                    promotedServices={data}
                    onSuccess={openEditDialog}
                    isEditMode={!!promotedService}
                    promotedService={promotedService}
                />
            ) : null}
        </ModalWrapper>
    );
};
